import { from, Observable } from 'rxjs';
import { debounce, noop } from 'lodash';
import { componentTreeSelectionChanged } from '@ardoq/component-selection';
import { colors } from '@ardoq/design-tokens';
import {
  enterPressed,
  moveHighlightRequested,
  setIsSelectAll,
} from '@ardoq/dropdown-menu';
import { dispatchAction, reducer, streamReducer } from '@ardoq/rxbeach';
import { Locale } from '@ardoq/locale';
import { ContextSelectorComponentItem } from './components/ContextSelectorComponentItem';
import { SelectWorkspaceItem } from './components/SelectWorkspaceItem';
import { getOnMouseOverHandler } from './getOnMouseOverHandler';
import { getPublishScrollHeight } from './routines/getPublishScrollHeight';
import { enhancedScopeDataStreamReducer } from './reducers/selectComponentStreamReducer';
import { getScrollHighlightedIntoViewRoutine } from './routines/getScrollHighlightedIntoViewRoutine';
import { actionNamespaceSelectContext } from '@ardoq/global-consts';
import { ComponentHierarchyState } from '../componentHierarchyTypes';
import { NavigatorConfiguration } from '../types';
import { navigatorLayoutOperations } from '../reducers/navigatorLayoutOperations';
import { navigatorSelectionOperations } from '../reducers/navigatorSelectionOperations';
import { navigatorOperations } from '../reducers/navigatorOperations';
import { newItemHovered } from '../actions';
import { navigatorTreeOperations } from '../reducers/navigatorTreeOperations';
import { getQuerySelectComponent$ } from './streams/querySelectComponent$';
import { enhancedScopeDataToHierarchyStreamNoDnD } from './enhancedScopeDataToHierarchyStreamNoDnD';
import { getSelectComponentHierarchySelection$ } from '../streams/selectComponentHierarchySelection$';
import {
  containerHeightChanged,
  initialComponentsSelectedInTree,
} from '../actions/navigatorActions';
import { getHandleSelectedComponentsOnOpeningDropdownRoutine } from './routines/getHandleSelectedComponentsOnOpeningDropdownRoutine';
import { getScopeDataStreamWithWorkspaceOptions } from '../streams/getScopeDataStreamWithWorkspaceOptions';
import { getClearSelectAllOnRangeSelectRoutine } from './routines/getClearSelectAllOnRangeSelectRoutine';
import { getContextSelectorSelectionRoutine } from './routines/getContextSelectorSelectionRoutine';
import { getSelectComponentSelectionChangedRoutine } from './routines/getSelectComponentSelectionChangedRoutine';

const querySelectComponent$ = getQuerySelectComponent$(
  actionNamespaceSelectContext
);

const {
  enhancedScopeData$,
  loadingScopeDataNotification$,
  loadScopeDataError$,
} = getScopeDataStreamWithWorkspaceOptions(
  querySelectComponent$,
  actionNamespaceSelectContext
);

const componentSelection$ = getSelectComponentHierarchySelection$(
  actionNamespaceSelectContext
);

const dispatchContainerHeightChanged = () =>
  dispatchAction(containerHeightChanged(), actionNamespaceSelectContext);

const debouncedHeightChanged = debounce(dispatchContainerHeightChanged, 16);

export const getSelectContextConfiguration = (
  locale: Locale
): NavigatorConfiguration => {
  const selectComponentHierarchy$: Observable<ComponentHierarchyState> =
    enhancedScopeDataToHierarchyStreamNoDnD({
      enhancedScopeData$,
      locale,
      isStrictSelection: true,
    });

  return {
    isMultiselectable: true,
    eventHandlers: {
      // The component is 'preselected' by hovering it so we already know which
      // component to select on enter press.
      onComponentClick: componentId =>
        dispatchAction(
          enterPressed({ nodeId: componentId, withMetaKey: false }),
          actionNamespaceSelectContext
        ),
      onMetaKeyClick: componentId =>
        dispatchAction(
          enterPressed({ nodeId: componentId, withMetaKey: true }),
          actionNamespaceSelectContext
        ),
      onWorkspaceClick: noop,
      onScenarioClick: noop,
      onWorkspaceCloseClick: noop,
      onScenarioCloseClick: noop,
      onComponentContextMenu: null,
      onWorkspaceContextmenu: null,
      onScenarioContextmenu: noop,
      onContextMenuClicked: noop,
      onMouseOver: getOnMouseOverHandler(),
    },
    additionalRoutines: [
      getPublishScrollHeight,
      getScrollHighlightedIntoViewRoutine,
      getContextSelectorSelectionRoutine,
      getClearSelectAllOnRangeSelectRoutine,
      getSelectComponentSelectionChangedRoutine,
      getHandleSelectedComponentsOnOpeningDropdownRoutine,
    ],
    externalReducers: [
      streamReducer(selectComponentHierarchy$, enhancedScopeDataStreamReducer),
      streamReducer(
        from([{ isViewpointMode: true }]),
        navigatorLayoutOperations.setIsViewpointMode
      ),

      streamReducer(
        componentSelection$,
        navigatorSelectionOperations.updateSelection
      ),
      streamReducer(
        loadingScopeDataNotification$,
        navigatorOperations.setDataLoadingMessage
      ),
      streamReducer(
        loadScopeDataError$,
        navigatorOperations.setDataErrorMessage
      ),
      reducer(newItemHovered, navigatorTreeOperations.newItemHovered),
      reducer(moveHighlightRequested, navigatorTreeOperations.moveHighlight),
      reducer(
        initialComponentsSelectedInTree,
        navigatorLayoutOperations.initialComponentsSelectedInTree
      ),
      reducer(setIsSelectAll, navigatorSelectionOperations.setIsAllSelected),
    ],
    componentSelection$,
    rangeSelectionMode: 'extendSelection',
    actionNamespace: actionNamespaceSelectContext,
    showWorkspaceCloseButton: true,
    showStartCreateReferenceIcon: false,
    backgroundColor: colors.white950,
    components: {
      ComponentItem: ContextSelectorComponentItem,
      WorkspaceItem: SelectWorkspaceItem,
      ScenarioItem: () => null,
    },
    onViewDestroyed: () => {
      dispatchAction(
        componentTreeSelectionChanged({ treeSelection: [] }),
        actionNamespaceSelectContext
      );
    },
    isControlledContainerHeight: false,
    useClassNameForRootComponents: true,
    onRootResize: debouncedHeightChanged,
  };
};
